import React, { useState, SyntheticEvent, useCallback } from 'react';
import { Modal, Button, Select, Input, Tooltip, Menu } from 'antd';
import { createTraining } from '../../../lib/api/training-api';
import { setFileAnnotations, deleteInference } from '../../../lib/api/aws-api';
import { createModel, deleteModel, getLatestAlgorithmVersion } from '../../../lib/api/algorithm-api';
import UploadContainer from '../upload/UploadContainer';
import { InferenceFile, Model, Training } from '../../../types';
import { FormItemLabel, ModalSubTitle, ModalTitle } from '../../../components/text/Titles';

const { Option } = Select;

type Props = {
  visible: boolean;
  onClose: () => any;
  onCreate: (model: Model, training: Training) => any;
  algorithmId: string;
};

const CreateClassificationModel = (props: Props) => {
  const { visible, onClose, onCreate, algorithmId } = props;

  const [classes, setClasses] = useState<string[]>([]);
  const [inferenceMap, setInferenceMap] = useState<{
    [className: string]: InferenceFile[];
  }>({});
  const [model, setModel] = useState<null | Model>(null);
  const [nameValue, setNameValue] = useState<string>('');
  const [step, setStep] = useState<0 | 1>(0);
  const [selectedClass, setSelectedClass] = useState<null | string>(null);

  const goToUploadEnabled = nameValue && nameValue.length > 0 && classes.length > 0;

  const submitEnabled = () => {
    if (inferenceMap[classes[0]] && inferenceMap[classes[0]].length === 0) return false;

    const atLeastOneUploadPerCase = classes.every(
      className => inferenceMap[className] && inferenceMap[className].length > 0
    );
    if (!atLeastOneUploadPerCase) return false;
    const uploadsCompleted = getInferenceList().every(inference => ['FINISHED', 'ERROR'].includes(inference.status));
    if (!uploadsCompleted) return false;
    return true;
  };

  const getInferenceList = useCallback(() => {
    return Object.values(inferenceMap).reduce((list, values) => [...list, ...values], []);
  }, [inferenceMap]);

  const rollbackCreation = useCallback(async () => {
    setModel(null);
    setInferenceMap({});
    try {
      if (model) {
        await deleteModel(model.id);
      }
      await Promise.all(getInferenceList().map(inference => deleteInference(inference.id)));
    } catch (e) {
      console.log(e);
    }
  }, [setModel, setInferenceMap, model, getInferenceList]);

  const onNameChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setNameValue(event.currentTarget.value);
  };

  const onClassChange = async (values: string[]) => {
    const removedClasses = classes.filter(className => !values.some(value => value === className));
    await Promise.all(
      removedClasses.map(async className => {
        const uploads = inferenceMap[className];
        if (!uploads || !uploads.length) return Promise.resolve();
        return Promise.all(uploads.map(inference => deleteInference(inference.id)));
      })
    );

    setClasses(values);
    setSelectedClass((values && values[0]) || null);
  };

  const goToUpload = async () => {
    const version = await getLatestAlgorithmVersion(algorithmId);
    const model = await createModel(nameValue, algorithmId, version.id, classes);
    setModel(model);
    setStep(1);
  };

  const onSubmit = async () => {
    if (!model) return;

    const training = await createTraining(
      model.id,
      getInferenceList().map(inference => inference.id),
      'Version 1.0'
    );

    // modify each inference with selected class
    await Promise.all(
      Object.keys(inferenceMap).map(key =>
        Promise.all(
          inferenceMap[key].map(inference =>
            setFileAnnotations(inference.id, {
              type: {
                reliable: true,
                name: key,
              },
              fields: [],
            })
          )
        )
      )
    );

    onModalClose(false);
    onCreate(model, training);
  };

  const onModalClose = async (remove: boolean) => {
    if (remove) {
      await rollbackCreation();
    }

    setClasses([]);
    setNameValue('');
    setStep(0);

    onClose();
  };

  const onInferenceListUpdate = (inferenceList: InferenceFile[], className: string) => {
    setInferenceMap({ ...inferenceMap, [className]: inferenceList });
  };

  const getInferencesStatusColor = (className: string) => {
    const inferenceList = inferenceMap[className] || [];
    if (inferenceList.find(inference => inference.status === 'ERROR')) {
      return '#fa541c';
    } else if (inferenceList.find(inference => inference.status === 'PROCESSING')) {
      return 'orange';
    } else {
      return '#52c41a';
    }
  };

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={<ModalTitle>New model</ModalTitle>}
      onCancel={() => onModalClose(true)}
      footer={
        step === 0 ? (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={goToUploadEnabled ? '' : 'Enter a name and select at least 1 label before continuing.'}>
              <Button style={{ marginLeft: '10px' }} type="primary" onClick={goToUpload} disabled={!goToUploadEnabled}>
                Next
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div>
            <Button type="default" onClick={() => onModalClose(true)}>
              Cancel
            </Button>
            <Tooltip title={submitEnabled() ? '' : 'Upload at least 1 document and wait for them to finish analysing.'}>
              <Button type="primary" onClick={onSubmit} style={{ marginLeft: '10px' }} disabled={!submitEnabled()}>
                Review
              </Button>
            </Tooltip>
          </div>
        )
      }>
      {step === 0 ? (
        <div>
          <FormItemLabel>This process will create a new model.</FormItemLabel>
          <Input placeholder="Name" value={nameValue} onChange={onNameChange} style={{ margin: '0px 0px 10px 0px' }} />
          <ModalSubTitle>Classes</ModalSubTitle>
          <FormItemLabel>Create up to 3 classes in which you’d like to classify your documents.</FormItemLabel>
          <Select
            defaultValue={classes}
            mode="tags"
            style={{ width: '100%', margin: '0px 0px 10px 0px' }}
            placeholder="Labels"
            onChange={onClassChange}>
            {classes.map(className => (
              <Option key={className} value={className}>
                {className}
              </Option>
            ))}
          </Select>
        </div>
      ) : (
        <div>
          {model && (
            <div>
              <FormItemLabel>
                We’ll be needing some example documents for each class to make an analysis. We advice you to upload up a
                minimum of 3 documents.
              </FormItemLabel>
              <Menu
                onClick={e => setSelectedClass(e.key)}
                selectedKeys={[selectedClass || '']}
                mode="horizontal"
                style={{ marginBottom: 20 }}>
                {classes.map(className => (
                  <Menu.Item key={className}>
                    {className} (
                    <span style={{ color: getInferencesStatusColor(className) }}>
                      {(inferenceMap[className] || []).length}
                    </span>
                    )
                  </Menu.Item>
                ))}
              </Menu>
              {classes.map(className => (
                <UploadContainer
                  hide={className !== selectedClass}
                  modelId={model.id}
                  inferenceList={inferenceMap[className] || []}
                  onInferenceListUpdated={inferenceList => onInferenceListUpdate(inferenceList, className)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateClassificationModel;
